import { useEffect, useState, useCallback } from "react";
import { Platform } from "react-native";
import { useRouter, useSegments, Slot, SplashScreen, router as rootRouter } from "expo-router";
import * as Font from "expo-font";
import { Ionicons } from "@expo/vector-icons";
import { AuthContext } from "./lib/auth";
import { setItem, getItem, removeItem } from "./lib/storage";
import * as api from "./lib/api";
import { LoadingScreen } from "../components/LoadingScreen";
import type { User } from "./lib/types";
import { registerForPushNotifications } from "./lib/pushNotifications";

// Lazy-load expo-notifications on native only. Wrapped in try/catch so OTA
// bundles targeting 1.4.x runtimes (no expo-notifications native module yet)
// don't crash on import — they just no-op.
let Notifications: any = null;
if (Platform.OS !== "web") {
  try {
    Notifications = require("expo-notifications");
  } catch {}
}

// Firebase Analytics — only imported on native (iOS/Android), not on web.
// Web has its own GA4 snippet baked into +html.tsx.
// try/catch so OTA bundles targeting older runtimes (1.4.11, pre-firebase native) don't crash.
let analytics: any = null;
if (Platform.OS !== "web") {
  try {
    analytics = require("@react-native-firebase/analytics").default;
  } catch {}
}

SplashScreen.preventAutoHideAsync();

// Fire a Firebase Analytics screen_view event whenever the route changes (native only).
function useScreenTracking() {
  const segments = useSegments();
  useEffect(() => {
    if (!analytics) return;
    const screenName = "/" + segments.filter(Boolean).join("/");
    analytics().logScreenView({
      screen_name: screenName || "/",
      screen_class: segments[0] || "root",
    }).catch(() => {});
  }, [segments]);
}

function useProtectedRoute(user: User | null, isLoading: boolean) {
  const segments = useSegments();
  const router = useRouter();

  useEffect(() => {
    if (isLoading) return;

    const inAuthGroup = segments[0] === "(auth)";
    // Trading card share URL — /dogs/:dogId/card — is public so teammates can view without login.
    const inPublicCardRoute = segments[0] === "dogs" && segments[2] === "card";
    const inPublicRoute = segments[0] === "dog" || segments[0] === "dashadmin" || segments[0] === "preview" || inPublicCardRoute;

    // Pages a logged-in user is still allowed to visit inside the (auth) group
    // (reached from Settings → Resources). The landing page stays excluded — it's
    // purely for converting signed-out visitors.
    // reset-password / forgot-password stay reachable while logged in so a user who
    // never signed out can still complete an emailed reset link (the email opens
    // /reset-password?token=…). Otherwise the guard would bounce them to (tabs).
    const inPublicAuthPage = inAuthGroup && (segments[1] === "support" || segments[1] === "privacy" || segments[1] === "terms" || segments[1] === "how-to-signup" || segments[1] === "blog" || segments[1] === "partners" || segments[1] === "reset-password" || segments[1] === "forgot-password" || segments[1] === "what-is-fast-cat" || segments[1] === "fast-cat-rankings" || segments[1] === "fast-cat-events" || segments[1] === "tools");

    if (!user && !inAuthGroup && !inPublicRoute) {
      router.replace("/(auth)");
    } else if (user && inAuthGroup && !inPublicAuthPage) {
      router.replace("/(tabs)");
    }
  }, [user, segments, isLoading]);
}

export default function RootLayout() {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fontsLoaded, setFontsLoaded] = useState(false);

  useEffect(() => {
    async function loadFonts() {
      try {
        // Load with exact name that @expo/vector-icons expects
        await Font.loadAsync({
          'ionicons': require('@expo/vector-icons/build/vendor/react-native-vector-icons/Fonts/Ionicons.ttf'),
        });
        setFontsLoaded(true);
      } catch (e) {
        console.error("Font loading error:", e);
        // Try fallback with Ionicons.font
        try {
          await Font.loadAsync(Ionicons.font);
        } catch {}
        setFontsLoaded(true);
      }
    }
    loadFonts();
  }, []);

  useEffect(() => {
    loadUser();
    // Let api.ts clear React user state when it force-signs-out (token refresh
    // fails after a long sleep, etc.) — without this, useProtectedRoute never
    // sees user go null and the user gets stuck on a broken screen.
    api.registerSignOutHandler(() => setUser(null));
  }, []);

  // Re-register push token on every authenticated launch so rotated tokens
  // keep working. Idempotent server-side. No-op if permission isn't granted yet.
  useEffect(() => {
    if (!user || Platform.OS === "web") return;
    registerForPushNotifications().catch(() => {});
  }, [user?.id]);

  // Notification tap → deep-link to the dog's public profile.
  useEffect(() => {
    if (!Notifications) return;
    const sub = Notifications.addNotificationResponseReceivedListener((response: any) => {
      const data = response?.notification?.request?.content?.data;
      if (data?.type === "follow_run" && (data?.slug || data?.dogId)) {
        const slug = data.slug || data.dogId;
        // Route to the followed dog's public profile. Using rootRouter (imported
        // singleton) since useRouter() isn't available inside the effect scope here.
        setTimeout(() => rootRouter.push(`/dog/${slug}` as any), 0);
      }
    });
    return () => sub.remove();
  }, []);

  async function loadUser() {
    try {
      const stored = await getItem("user");
      if (stored) {
        setUser(JSON.parse(stored));
      }
    } catch {} finally {
      setIsLoading(false);
    }
  }

  const signIn = useCallback(async (email: string, password: string) => {
    const res = await api.login(email, password);
    // On native, store tokens locally. On web, cookies handle auth.
    if (Platform.OS !== "web") {
      await setItem("accessToken", res.accessToken);
      await setItem("refreshToken", res.refreshToken);
    }
    await setItem("user", JSON.stringify(res.user));
    setUser(res.user);
  }, []);

  const signUp = useCallback(async (email: string, password: string, name: string, state?: string, zipCode?: string) => {
    const res = await api.register(email, password, name, state, zipCode);
    if (Platform.OS !== "web") {
      await setItem("accessToken", res.accessToken);
      await setItem("refreshToken", res.refreshToken);
    }
    await setItem("user", JSON.stringify(res.user));
    setUser(res.user);
  }, []);

  const signOut = useCallback(async () => {
    await api.logout();
    await removeItem("accessToken");
    await removeItem("refreshToken");
    await removeItem("user");
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const profile = await api.getProfile();
      setUser((prev) => {
        const next = { ...(prev ?? {} as User), id: profile.id, email: profile.email, name: profile.name };
        setItem("user", JSON.stringify(next)).catch(() => {});
        return next;
      });
    } catch {}
  }, []);

  useProtectedRoute(user, isLoading);
  useScreenTracking();

  useEffect(() => {
    if (!isLoading && fontsLoaded) {
      SplashScreen.hideAsync();
    }
  }, [isLoading, fontsLoaded]);

  if (isLoading || !fontsLoaded) return <LoadingScreen />;

  return (
    <AuthContext.Provider value={{ user, isLoading, signIn, signUp, signOut, refreshUser }}>
      <Slot />
    </AuthContext.Provider>
  );
}
